import {
  ApolloCache,
  ApolloClient,
  gql,
  type Reference,
  type StoreObject,
} from "@apollo/client";

import {
  EXECUTION_LIST_FRAGMENT,
  OBJECT_FRAGMENT,
} from "@neoledger/graphql/fragments";
import { Queries } from "@/constants/queries";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { State } from "@/state";
import type { ThreadIdType } from "@/state/@types";

/**
 * Whether the queue has a payment loaded - its count of its payee's others,
 * which only the queue's row and detail read: only one it shows is read again.
 */
const IN_THE_QUEUE_FRAGMENT = gql`
  fragment TriageInTheQueue on TransactionObject {
    id
    payeePaymentCount
  }
`;

const EXECUTION_PATCH_THREAD_ENTITY_FRAGMENT = gql`
  fragment ExecutionPatchThreadEntity on ThreadObject {
    entity {
      id
      identifier
      name
    }
  }
`;

export type ObjectChangeNode = {
  id?: string;
  identifier?: string;
  __typename?: string;
  status?: string | null;
  completedAt?: string | null;
  error?: string | null;
  triggerType?: string | null;
  startedAt?: string | null;
  updatedAt?: string | null;
  rule?: { identifier?: string } | null;
  [key: string]: unknown;
};

export type ObjectChange = {
  action?: string;
  globalId?: string;
  typename?: string;
  node?: ObjectChangeNode;
  related?: ObjectChange[];
};

export type ExecutionLivePatch = {
  status?: string | null;
  completedAt?: string | null;
  error?: string | null;
  triggerType?: string | null;
  startedAt?: string | null;
};

const EXECUTION_LIVE_FIELDS = [
  "status",
  "completedAt",
  "error",
  "triggerType",
  "startedAt",
  "updatedAt",
] as const;

function resolveTypename(change: ObjectChange): string | null {
  return change.node?.__typename ?? change.typename ?? null;
}

function executionPatchFromNode(
  node: ObjectChangeNode | undefined,
): ExecutionLivePatch {
  const patch: ExecutionLivePatch = {};
  if (!node) return patch;
  if (typeof node.status === "string") patch.status = node.status;
  if (node.completedAt !== undefined) patch.completedAt = node.completedAt;
  if (node.error !== undefined) patch.error = node.error;
  if (node.triggerType !== undefined) patch.triggerType = node.triggerType;
  if (node.startedAt !== undefined) patch.startedAt = node.startedAt;
  return patch;
}

function mergeExecutionPatch(
  patches: Record<string, ExecutionLivePatch>,
  key: string,
  patch: ExecutionLivePatch,
) {
  if (Object.keys(patch).length === 0) return;
  patches[key] = { ...patches[key], ...patch };
}

function executionCacheId(
  cache: ApolloCache,
  change: ObjectChange,
): string | null {
  const node = change.node;
  if (node?.id) {
    return (
      cache.identify({
        __typename: "ExecutionObject",
        id: node.id,
      }) ?? node.id
    );
  }
  return change.globalId ?? null;
}

/** Merge execution lifecycle fields without clobbering unrelated cache data. */
function patchExecutionObjectInCache(cache: ApolloCache, change: ObjectChange) {
  const node = change.node;
  if (resolveTypename(change) !== "ExecutionObject" || !node?.id) return;

  const cacheId = executionCacheId(cache, change);
  if (!cacheId) return;

  // Partial ``objectsChanged`` payloads must not use ``writeFragment`` with
  // ``Execution`` / ``ExecutionListItem`` — those fragments always request
  // ``rule`` and ``thread`` (and ``thread.entity``), which triggers
  // "Missing field …" when the event omits them. Field modifiers touch only
  // scalars already stored on the normalised execution node.
  const fields: Record<string, (existing: unknown) => unknown> = {};
  for (const key of EXECUTION_LIVE_FIELDS) {
    if (node[key] !== undefined) {
      fields[key] = () => node[key];
    }
  }
  if (Object.keys(fields).length === 0) return;

  cache.modify({
    id: cacheId,
    fields,
  });
}

/** Seed a newly created execution row for connection list views. */
function writeExecutionCreatedToCache(
  cache: ApolloCache,
  change: ObjectChange,
) {
  const node = change.node;
  if (!node?.id) return;

  const cacheId = executionCacheId(cache, change);
  if (!cacheId) return;

  const data: Record<string, unknown> = {
    __typename: "ExecutionObject",
    id: node.id,
    rule: node.rule ?? null,
  };

  for (const key of [
    "identifier",
    "createdAt",
    "updatedAt",
    "source",
    "status",
    "triggerType",
    "startedAt",
    "completedAt",
    "error",
    "seedContext",
  ] as const) {
    if (node[key] !== undefined) data[key] = node[key];
  }

  const enrichedThread = enrichThreadForListFragment(cache, node.thread);
  if (!enrichedThread) {
    patchExecutionObjectInCache(cache, change);
    return;
  }

  data.thread = enrichedThread;

  cache.writeFragment({
    id: cacheId,
    fragment: EXECUTION_LIST_FRAGMENT,
    fragmentName: "ExecutionListItem",
    data,
  });
}

function enrichThreadForListFragment(
  cache: ApolloCache,
  thread: ObjectChangeNode["thread"],
): Record<string, unknown> | undefined {
  if (!thread || typeof thread !== "object") return undefined;
  const row = thread as {
    id?: string;
    identifier?: string;
    name?: string;
    entity?: { id?: string };
    __typename?: string;
  };
  if (!row.id) return undefined;
  if (row.entity?.id) {
    return { __typename: "ThreadObject", ...row };
  }

  const threadCacheId = cache.identify({
    __typename: "ThreadObject",
    id: row.id,
  });
  if (!threadCacheId) return undefined;

  const existing = cache.readFragment<{
    entity?: { id: string; identifier: string; name: string };
  }>({
    id: threadCacheId,
    fragment: EXECUTION_PATCH_THREAD_ENTITY_FRAGMENT,
  });
  if (!existing?.entity) return undefined;

  return {
    __typename: "ThreadObject",
    id: row.id,
    identifier: row.identifier,
    name: row.name,
    entity: existing.entity,
  };
}

/** Pull execution lifecycle fields out of an ``objectsChanged`` batch. */
export function extractExecutionLivePatches(
  changes: ObjectChange[],
): Record<string, ExecutionLivePatch> {
  const patches: Record<string, ExecutionLivePatch> = {};

  const visit = (change: ObjectChange) => {
    if (resolveTypename(change) !== "ExecutionObject") {
      for (const rel of change.related ?? []) visit(rel);
      return;
    }

    const patch = executionPatchFromNode(change.node);
    const keys = new Set<string>();
    if (change.node?.id) keys.add(change.node.id);
    if (change.globalId) keys.add(change.globalId);
    if (change.node?.identifier) keys.add(change.node.identifier);
    for (const key of keys) mergeExecutionPatch(patches, key, patch);

    for (const rel of change.related ?? []) visit(rel);
  };

  for (const change of changes) visit(change);
  return patches;
}

export function changesIncludeExecutionObject(
  changes: ObjectChange[],
): boolean {
  const visit = (change: ObjectChange): boolean => {
    if (change.typename === "ExecutionObject") return true;
    return (change.related ?? []).some(visit);
  };
  return changes.some(visit);
}

/** Materialise an ``objectsChanged`` node payload into Apollo's normalised cache. */
export function writeObjectChangeToCache(
  client: ApolloClient,
  change: ObjectChange,
) {
  writeObjectChangeToCacheWithCache(client.cache, change);
}

export function writeObjectChangeToCacheWithCache(
  cache: ApolloCache,
  change: ObjectChange,
) {
  const node = change.node;
  const typename = resolveTypename(change);
  if (!node?.id || !typename) return;

  // Booked, a payment leaves the queue: one moved to another business with
  // nothing booked of it but its move has an entry, and is still waiting. It
  // stays in a list that asks for the booked - the Booked queue's, the
  // missing documents', a payee's others - however it changes there.
  if (
    typename === "TransactionObject" &&
    (node as { isBooked?: boolean | null }).isBooked === true
  ) {
    removeNodesFromConnections(
      cache,
      "triageConnection",
      [node.id],
      undefined,
      (storeFieldName) => !storeFieldName.includes('"includesBooked":true'),
    );
  }

  if (typename === "ExecutionObject") {
    if (change.action === "created") {
      writeExecutionCreatedToCache(cache, change);
    } else {
      patchExecutionObjectInCache(cache, change);
    }
    return;
  }

  const cacheId =
    cache.identify({
      __typename: typename,
      id: node.id,
    }) ??
    change.globalId ??
    null;
  if (!cacheId) return;

  cache.writeFragment({
    id: cacheId,
    fragment: OBJECT_FRAGMENT,
    fragmentName: "Object",
    data: { __typename: typename, ...node },
  });
}

/**
 * Drop deleted rows from every cached page of a relay-style connection field
 * - or, given *isFromList*, from the lists of it whose arguments (its
 * ``storeFieldName``) it says.
 */
export function removeNodesFromConnections(
  cache: ApolloCache,
  connectionFieldName: string,
  nodeIds: Iterable<string>,
  nodeTypename?: string,
  isFromList?: (storeFieldName: string) => boolean,
) {
  const ids = new Set(nodeIds);
  if (ids.size === 0) return;

  cache.modify({
    fields: {
      [connectionFieldName](
        existing: Record<string, unknown> | null,
        { readField, storeFieldName },
      ) {
        if (!existing) return existing;
        if (isFromList && !isFromList(storeFieldName)) return existing;

        let mutated = false;
        const next: Record<string, unknown> = { ...existing };

        for (const key of Object.keys(existing)) {
          if (!key.startsWith("data")) continue;

          const page = existing[key] as
            | {
                edges?: Array<{ node?: unknown }>;
                totalCount?: number;
              }
            | undefined;
          if (!page || !Array.isArray(page.edges)) continue;

          const edges = page.edges.filter((edge) => {
            const nodeId = edge?.node
              ? (readField("id", edge.node as Reference | StoreObject) as
                  | string
                  | undefined)
              : undefined;
            return !nodeId || !ids.has(nodeId);
          });

          if (edges.length === page.edges.length) continue;

          const removedCount = page.edges.length - edges.length;
          next[key] = {
            ...page,
            totalCount: Math.max(
              0,
              (page.totalCount ?? page.edges.length) - removedCount,
            ),
            edges,
          };
          mutated = true;
        }

        return mutated ? next : existing;
      },
    },
  });

  if (!nodeTypename) return;

  for (const id of ids) {
    const cacheId = cache.identify({ __typename: nodeTypename, id });
    if (cacheId) {
      cache.evict({ id: cacheId });
    }
  }
  cache.gc();
}

/** Prepend a new execution row into matching ``executionsConnection`` caches. */
export function spliceExecutionIntoConnections(
  cache: ApolloCache,
  execution: ObjectChangeNode,
  ruleIdentifier?: string | null,
  nodeRef?: string | Reference,
) {
  if (!execution?.id || !execution.identifier) return;

  const wfId =
    ruleIdentifier ??
    (execution.rule as { identifier?: string } | null | undefined)
      ?.identifier ??
    null;

  const resolvedNodeRef =
    nodeRef ??
    cache.identify({
      __typename: "ExecutionObject",
      id: execution.id,
    }) ??
    execution.id;

  cache.modify({
    fields: {
      executionsConnection(existing: any, { storeFieldName, readField }) {
        if (!existing) return existing;
        if (wfId && !storeFieldName.includes(JSON.stringify(wfId))) {
          return existing;
        }

        let mutated = false;
        const next: any = { ...existing };
        for (const key of Object.keys(existing)) {
          if (!key.startsWith("data")) continue;
          const page = existing[key];
          if (!page || !Array.isArray(page.edges)) continue;

          const alreadyPresent = page.edges.some((edge: any) => {
            const identifier = edge?.node
              ? readField("identifier", edge.node)
              : null;
            return identifier === execution.identifier;
          });
          if (alreadyPresent) continue;

          next[key] = {
            ...page,
            totalCount: (page.totalCount ?? 0) + 1,
            edges: [
              {
                __typename: "ExecutionObjectEdge",
                cursor: "",
                node: resolvedNodeRef,
              },
              ...page.edges,
            ],
          };
          mutated = true;
        }
        return mutated ? next : existing;
      },
    },
  });
}

export function applyObjectChangesToCache(
  client: ApolloClient,
  changes: ObjectChange[],
) {
  let isNotified = false;
  let isDocumentChanged = false;
  let isSuggestionChanged = false;
  // The payments whose suggestions were worked out again, open or not.
  const reweighed = new Set<string>();
  const visit = (change: ObjectChange) => {
    // Turned down, a draft is deleted; worked out again, what a payment would
    // make of one - its suggestion in review - is too.
    switch (resolveTypename(change)) {
      case "DocumentObject":
        isDocumentChanged = true;
        break;
      case "RuleSuggestionObject": {
        isSuggestionChanged = true;
        const payment = (
          change.node as { transaction?: { identifier?: string } } | null
        )?.transaction;
        if (payment?.identifier) {
          reweighed.add(payment.identifier);
        }
        break;
      }
    }
    if (change.action === "deleted" && change.globalId) {
      client.cache.evict({ id: change.globalId });
      if (resolveTypename(change) === "ThreadObject") {
        State.getState().closeThreadTab(change.globalId as ThreadIdType);
      }
      return;
    }
    writeObjectChangeToCache(client, change);
    if (
      change.action === "created" &&
      resolveTypename(change) === "ExecutionObject" &&
      change.node?.id &&
      change.node.identifier
    ) {
      const ruleIdentifier =
        (change.node.rule as { identifier?: string } | null | undefined)
          ?.identifier ?? null;
      spliceExecutionIntoConnections(client.cache, change.node, ruleIdentifier);
    }
    if (
      change.action === "created" &&
      resolveTypename(change) === "NotificationObject"
    ) {
      isNotified = true;
    }
    for (const rel of change.related ?? []) visit(rel);
  };

  for (const change of changes) visit(change);
  // A notification just made - a forwarded email's - is counted and listed by
  // the bell, which asks for them again: nothing else puts it in its list.
  if (isNotified) {
    void client.refetchQueries({ include: [Queries.INBOX_CONNECTION_QUERY] });
  }
  // A document's lines are read apart from it, so they are read again as it
  // changes - its file read again in the background writes new ones - where
  // they are on screen: its panel's Line Items, a match's lines.
  if (isDocumentChanged) {
    void client.refetchQueries({
      include: [Queries.DOCUMENT_LINE_ITEMS_QUERY],
    });
  }
  // A payment's suggestions worked out again in the background - a draft
  // confirmed in its chat becomes the receipt they offer, or books the payment
  // as it is - with nothing marking the payment busy, so nothing else says
  // they changed: the payment open in triage reads them again. Its row's chips
  // read the same list. A document changed may be such a draft, word of it
  // come before word of its suggestion: read again for it too.
  const suggestionsRead =
    isSuggestionChanged || isDocumentChanged
      ? client.refetchQueries({ include: [Queries.TRIAGE_DETAIL_QUERY] })
      : null;
  // A payment booked teaches the payments from its payee, worked out again in
  // the background: each is read again where the queue has it - its row's
  // chips, and its panel, prefetched or not - not only the one open. And the
  // others of the open payment's payee, offered beside its Book, are asked
  // again.
  for (const identifier of reweighed) {
    const id = identifierAndTypenameToId(identifier, "TransactionObject");
    const cached = client.cache.identify({
      __typename: "TransactionObject",
      id,
    });
    if (
      cached &&
      client.cache.readFragment({ id: cached, fragment: IN_THE_QUEUE_FRAGMENT })
    ) {
      void client.query({
        query: Queries.TRIAGE_DETAIL_QUERY,
        variables: { id },
        fetchPolicy: "network-only",
      });
    }
  }
  if (isSuggestionChanged) {
    void client.refetchQueries({ include: [Queries.TRIAGE_BOOK_LIKE_QUERY] });
  }
  // A document drafted in a chat - a forwarded receipt read by the AI -
  // confirmed or turned down there, or a suggestion of one in review weighed
  // again: the payment open in triage reads the drafts it would take again.
  // Asked for only while it is open, and after its suggestions, so a draft
  // gives way to the one it makes in one go - never to nothing in between.
  if (isDocumentChanged || isSuggestionChanged) {
    const draftsRead = () => {
      void client.refetchQueries({
        include: [Queries.TRIAGE_DRAFT_MATCHES_QUERY],
      });
    };
    if (suggestionsRead) {
      void suggestionsRead.then(draftsRead, draftsRead);
    } else {
      draftsRead();
    }
  }
}
